import { APIConfiguration } from '@savgroup-front-common/configuration';
import {
  DOCUMENT_TYPES,
  SUPPORTED_METHODS,
} from '@savgroup-front-common/constants';
import {
  buildUrl,
  prepareResponseFailure,
} from '@savgroup-front-common/core/helpers';
import { apiCall } from '@savgroup-front-common/core/services';
import {
  ADDITIONAL_INFORMATION_TYPES,
  BackResponse,
} from '@savgroup-front-common/types';

export const FILE_ACTIVITY_TYPE = {
  WorkflowStatus: 'WorkflowStatus',
  AddInfo: 'AddInfo',
  Comment: 'Comment',
  Error: 'Error',
  Notification: 'Notification',
  Attachment: 'Attachment',
  WorkflowInternal: 'WorkflowInternal',
  FraudReport: 'FraudReport',
  Assignment: 'Assignment',
  Other: 'Other',
} as const;

export type FileActivityType =
  (typeof FILE_ACTIVITY_TYPE)[keyof typeof FILE_ACTIVITY_TYPE];

export const FILE_ACTIVITY_SUB_TYPE = {
  InternalComment: 'InternalComment',
  ToCustomerComment: 'ToCustomerComment',
  Email: 'Email',
  Sms: 'Sms',
  ExternalSystemNotification: 'ExternalSystemNotification',
  VideoSession: 'VideoSession',
  DocumentGenerated: 'DocumentGenerated',
  FileAttachment: 'FileAttachment',
  Label: 'Label',
  ActorSetting: 'ActorSetting',
} as const;

export type FileActivitySubType =
  (typeof FILE_ACTIVITY_SUB_TYPE)[keyof typeof FILE_ACTIVITY_SUB_TYPE];

export const FILE_ACTIVITY_GENERIC_ACTION = {
  Created: 'Created',
  Updated: 'Updated',
  Deleted: 'Deleted',
} as const;

export type FileActivityGenericAction =
  (typeof FILE_ACTIVITY_GENERIC_ACTION)[keyof typeof FILE_ACTIVITY_GENERIC_ACTION];

export interface MailRequestMessageEmailDto {
  email: string;
  name: string;
}

export interface MailRequestMessageAttachmentDto {
  filename: string;
  contentType: string;
  base64Content: string;
}

export interface MailRequestMessageInlinedAttachmentDto extends MailRequestMessageAttachmentDto {
  contentId: string;
}

export interface MailRequestMessage {
  from: MailRequestMessageEmailDto;
  sender: MailRequestMessageEmailDto;
  to: MailRequestMessageEmailDto[];
  cc: MailRequestMessageEmailDto[];
  bcc: MailRequestMessageEmailDto[];
  replyTo: MailRequestMessageEmailDto;
  subject: string;
  textPart: string;
  htmlPart: string;
  attachments: MailRequestMessageAttachmentDto[];
  inlinedAttachments: MailRequestMessageInlinedAttachmentDto[];
  variables: string;
}

export interface MailRequest {
  messages: MailRequestMessage[];
}

export interface MailResponseMessageEmail {
  email: string;
  messageUUID: string;
  messageId: string;
  messageHref: string;
}

export interface MailResponseError {
  errorIdentifier: string;
  errorCode: string;
  statusCode: number;
  errorMessage: string;
  errorRelatedTo: string;
}

export interface MailResponseMessage {
  status: string;
  to: MailResponseMessageEmail[];
  cc: MailResponseMessageEmail[];
  bcc: MailResponseMessageEmail[];
  errors: MailResponseError[];
}

export interface MailResponse {
  messages: MailResponseMessage[];
}

export interface MailTemplateResponseHeaders {
  from: string;
  subject: string;
  replyTo: string;
}

export interface MailTemplateResponse {
  headers: MailTemplateResponseHeaders;
  htmlPart: string;
  mjmlContent: string;
  textPart: string;
  templateName: string;
}

export interface MailMessageDto {
  from?: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  subject?: string;
  templateReference?: string;
  basicMail?: boolean;
  basicMailTextPart?: string;
  basicMailHTMLPart?: string;
  urlsToAttach?: string[];
  attachmentsBlobNames?: string[];
  model?: string;
  auth?: string;
  recipientEntityType?: string;
}

export interface MailNotificationHistoryDto {
  message: MailMessageDto;
  request: MailRequest;
  response: MailResponse;
  templateData: MailTemplateResponse;
  createdAt: string;
  id: string;
}

export interface AdditionalInformationHistoryDto {
  fileActivityId: string;
  additionalInformationStringValue?: string;
  additionalInformationId: string;
  additionalInformationFileValue?: {
    fileName: string;
    url: string;
    uploaderId?: string;
    uploaderFullName?: string;
    isUploadedByOwner?: boolean;
    uploadDateUtc?: string;
    customerVisibility?: boolean;
  };
  additionalInformationFilesValue?: {
    fileName: string;
    url: string;
  }[];
  additionalInformation?: {
    informationType: ADDITIONAL_INFORMATION_TYPES;
    label: string;
  };
  additionalInformationEnum?: {
    label: string;
  };
}

export interface UserDetailsDto {
  fullName?: string;
  rolesAndProfiles?: string[];
}

export interface BaseAdvancedHistoryActivityDto {
  activityId: string;
  parentActivityId?: string;
  activityDate: string;
  label?: string;
  user?: string;
  userDetails?: UserDetailsDto;
  genericAction?: FileActivityGenericAction;
  activities?: AdvancedHistoryActivityDto[];
}

export type AdvancedHistoryActivityMailNotificationDto =
  BaseAdvancedHistoryActivityDto & {
    activityType: typeof FILE_ACTIVITY_TYPE.Notification;
    activitySubType?: typeof FILE_ACTIVITY_SUB_TYPE.Email;
    detail?: { emailNotification: MailNotificationHistoryDto };
  };

export type AdvancedHistoryActivityCommentDto =
  BaseAdvancedHistoryActivityDto & {
    activityType: typeof FILE_ACTIVITY_TYPE.Comment;
    activitySubType?:
      | typeof FILE_ACTIVITY_SUB_TYPE.InternalComment
      | typeof FILE_ACTIVITY_SUB_TYPE.ToCustomerComment;
    detail?: { comment: string };
  };

export type AdvancedHistoryActivityAdditionalInformationDto =
  BaseAdvancedHistoryActivityDto & {
    activityType: typeof FILE_ACTIVITY_TYPE.AddInfo;
    detail: {
      additionalInformations: AdditionalInformationHistoryDto[];
    };
  };

export type AdvancedHistoryActivityAttachmentDto =
  BaseAdvancedHistoryActivityDto & {
    activityType: typeof FILE_ACTIVITY_TYPE.Attachment;
    activitySubType:
      | typeof FILE_ACTIVITY_SUB_TYPE.FileAttachment
      | typeof FILE_ACTIVITY_SUB_TYPE.DocumentGenerated;
    detail: {
      fileName: string;
      url: string;
      documentType?: DOCUMENT_TYPES;
    };
  };

export type AdvancedHistoryActivityErrorDto = BaseAdvancedHistoryActivityDto & {
  activityType: typeof FILE_ACTIVITY_TYPE.Error;
  detail?: {
    errorMessage?: string;
  };
};

export interface WorkflowStatusModuleDto {
  id: string;
  definitionId: string;
  wave: number;
  parameters: Record<string, unknown>;
  referencedModuleType: string;
}

export interface WorkflowStatusStateDto {
  name: string;
  shortLabel?: string;
  label?: string;
}

export type AdvancedHistoryActivityWorkflowStatusDto =
  BaseAdvancedHistoryActivityDto & {
    activityType: typeof FILE_ACTIVITY_TYPE.WorkflowStatus;
    activitySubType?: FileActivitySubType;
    detail?: {
      fileState?: {
        module?: WorkflowStatusModuleDto;
        state?: WorkflowStatusStateDto;
      };
    };
  };

export type AdvancedHistoryActivityDto =
  | (BaseAdvancedHistoryActivityDto & {
      activityType: Exclude<
        FileActivityType,
        | typeof FILE_ACTIVITY_TYPE.Notification
        | typeof FILE_ACTIVITY_TYPE.Comment
        | typeof FILE_ACTIVITY_TYPE.AddInfo
        | typeof FILE_ACTIVITY_TYPE.Attachment
        | typeof FILE_ACTIVITY_TYPE.WorkflowStatus
      >;
      activitySubType?: FileActivitySubType;
    })
  | AdvancedHistoryActivityMailNotificationDto
  | AdvancedHistoryActivityCommentDto
  | AdvancedHistoryActivityAdditionalInformationDto
  | AdvancedHistoryActivityAttachmentDto
  | AdvancedHistoryActivityErrorDto
  | AdvancedHistoryActivityWorkflowStatusDto;

export interface GetAdvancedHistoryResponse {
  activities?: AdvancedHistoryActivityDto[];
}

const ENDPOINT = `${APIConfiguration.workflow}files/customerfile`;

export const getAdvancedHistoryQuery = async ({
  fileId,
  language,
}: {
  fileId: string;
  language?: string;
}) => {
  try {
    return await apiCall<BackResponse<GetAdvancedHistoryResponse>>(
      buildUrl(`${ENDPOINT}/${fileId}/advancedHistory`, {
        Language: language,
      }),
      { method: SUPPORTED_METHODS.GET },
    );
  } catch (error) {
    return prepareResponseFailure(error);
  }
};

export const isAdvancedHistoryActivityMailNotificationDto = (
  activity: AdvancedHistoryActivityDto,
): activity is AdvancedHistoryActivityMailNotificationDto => {
  return (
    activity.activityType === FILE_ACTIVITY_TYPE.Notification &&
    activity.activitySubType === FILE_ACTIVITY_SUB_TYPE.Email
  );
};
export const isAdvancedHistoryActivityCommentDto = (
  activity: AdvancedHistoryActivityDto,
): activity is AdvancedHistoryActivityCommentDto => {
  return (
    activity.activityType === FILE_ACTIVITY_TYPE.Comment &&
    (activity.activitySubType === FILE_ACTIVITY_SUB_TYPE.InternalComment ||
      activity.activitySubType === FILE_ACTIVITY_SUB_TYPE.ToCustomerComment)
  );
};
export const isAdvancedHistoryActivityAdditionalInformationDto = (
  activity: AdvancedHistoryActivityDto,
): activity is AdvancedHistoryActivityAdditionalInformationDto => {
  return activity.activityType === FILE_ACTIVITY_TYPE.AddInfo;
};

export const isAdvancedHistoryActivityAttachmentDto = (
  activity: AdvancedHistoryActivityDto,
): activity is AdvancedHistoryActivityAttachmentDto => {
  return activity.activityType === FILE_ACTIVITY_TYPE.Attachment;
};

export const isAdvancedHistoryActivityWorkflowStatusDto = (
  activity: AdvancedHistoryActivityDto,
): activity is AdvancedHistoryActivityWorkflowStatusDto => {
  return activity.activityType === FILE_ACTIVITY_TYPE.WorkflowStatus;
};
